import { Toaster as Sonner, toast } from "sonner"
import { useIsMobile } from "@/hooks/use-mobile"

type ToasterProps = React.ComponentProps<typeof Sonner>

/**
 * Toaster — conformed to the design system's Toast spec (Claude Design →
 * components/feedback/Toast). What the spec pins:
 *
 *   --color-surface-raised fill · --r-lg radius · --shadow-card-large
 *   a semantic hairline in the TONE colour, not a neutral border
 *   title 14/20 600 --color-text-primary · message 13/20 --color-text-secondary
 *   action --color-text-accent · close --color-text-muted
 *
 * The previous styling used Tailwind's generic semantics — `bg-background`,
 * `border-border`, `shadow-lg` — which are near-misses rather than the tokens.
 *
 * One deliberate departure from the spec (2026-08-17, by request): the tone
 * hairline uses the -subtle 50-level status tokens, not the full-strength
 * colours the spec names. The saturated ring read as a hard contrasting
 * border; elevation now comes from --shadow-card-large with the border as a
 * faint tint. Tone stays legible through sonner's status icon, which keeps
 * the full-strength colour.
 *
 * Sonner owns the markup, so conformance goes through `classNames` rather than
 * a rewrite. Its per-type keys (`success`, `error`, …) are what let the tone
 * hairline reach the right toast.
 *
 * Two spec details deliberately NOT forced:
 *
 * - **`--ease-overshoot` on entry.** The spec calls this the only component
 *   permitted that easing. Sonner animates via its own bundled stylesheet and
 *   inline transforms, so overriding it means fighting internals with
 *   `!important` and re-breaking on every sonner upgrade. Flagged rather than
 *   forced; it needs either a sonner-level option or a decision to hand-roll
 *   the toast.
 * - **The 420px fixed width.** Sonner sizes toasts itself and this app shows
 *   them top-center on mobile, where 420px would clip.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const isMobile = useIsMobile()

  return (
    <Sonner
      // App ships light-only (dark: utilities were removed app-wide and no
      // next-themes provider is mounted). Pin to light so a system-dark OS
      // can't surface a stray dark toast.
      theme="light"
      // Top-center on mobile (thumb-reachable, won't clip off the right edge);
      // top-right on desktop. An explicit `position` prop still overrides this.
      position={isMobile ? "top-center" : "top-right"}
      // Every popup gets a dismiss cross — waiting out or swiping a toast was
      // previously the only way to clear one.
      closeButton
      className="toaster group"
      toastOptions={{
        classNames: {
          // `border` Tailwind utility is required alongside the colour —
          // Tailwind's preflight zeroes `border-width` on every element, and
          // Sonner's own `border: 1px solid …` rule lives inside `:where(...)`
          // (zero specificity) so preflight wins. Without explicit `border`
          // here the toast renders as a borderless white slab.
          //
          // Surface, radius and shadow are the spec's; the border colour is a
          // neutral default that each tone below overrides.
          toast: [
            "group toast border",
            "group-[.toaster]:bg-[var(--color-surface-raised)]",
            "group-[.toaster]:text-[var(--color-text-primary)]",
            "group-[.toaster]:border-[var(--color-border-default)]",
            "group-[.toaster]:rounded-[var(--r-lg)]",
            "group-[.toaster]:shadow-[var(--shadow-card-large)]",
          ].join(" "),
          title:
            "group-[.toast]:text-[length:14px] group-[.toast]:font-semibold group-[.toast]:text-[var(--color-text-primary)]",
          description: "group-[.toast]:text-[var(--color-text-secondary)]",
          actionButton:
            "group-[.toast]:bg-transparent group-[.toast]:font-semibold group-[.toast]:text-[var(--color-text-accent)]",
          cancelButton:
            "group-[.toast]:bg-transparent group-[.toast]:text-[var(--color-text-muted)]",
          // Sonner reveals its close button on hover by default, which reads as
          // "no way to dismiss" on touch. Pin it visible.
          closeButton:
            "group-[.toast]:text-[var(--color-text-muted)] group-[.toast]:opacity-100 group-[.toast]:bg-[var(--color-surface-raised)] group-[.toast]:border-[var(--color-border-default)]",

          // The tone hairline, on the -subtle (50-level) status tokens. The
          // full-strength colours drew a hard saturated ring around every
          // toast; at 50-level the tint is a whisper and the card reads as
          // shadow-carried instead of outlined. The tone signal survives:
          // sonner's own status icon is still drawn in the full-strength
          // colour, so success and failure stay distinguishable at a glance —
          // the icon carries the tone, the border merely agrees with it.
          success: "group-[.toaster]:border-[var(--color-status-success-subtle)]",
          error: "group-[.toaster]:border-[var(--color-status-danger-subtle)]",
          warning: "group-[.toaster]:border-[var(--color-status-warning-subtle)]",
          info: "group-[.toaster]:border-[var(--color-status-info-subtle)]",
        },
      }}
      {...props}
    />
  )
}

export { Toaster, toast }
